<template>
  <button
    v-bind="$attrs"
    type="button"
    class="button is-primary"
    :class="{ 'is-loading': loading }"
    :disabled="loading || undefined"
    v-on:click="toggleModal"
  >
    Create
  </button>
  <Modal
    is-large
    v-model="modal"
    title="New secret"
  >
    <form
      id="create-secret"
      v-on:submit.prevent="performCreate"
    >
      <div class="field">
        <label class="label">Name</label>
        <div class="control">
          <input
            autofocus
            class="input"
            :class="{ 'is-danger': errors.name }"
            type="text"
            placeholder="Secret name"
            v-model.trim="name"
            required
          />
        </div>
        <template v-if="errors.name">
          <p
            class="help is-danger"
            v-for="err in errors.name"
            :key="err"
          >
            {{ err }}
          </p>
        </template>
      </div>
      <div class="field">
        <label class="label">Content</label>
        <div class="control">
          <textarea
            class="textarea"
            :class="{ 'is-danger': errors.content }"
            type="text"
            placeholder="Secret contents"
            v-model.trim="content"
            required
          ></textarea>
        </div>
        <template v-if="errors.content">
          <p
            class="help is-danger"
            v-for="err in errors.content"
            :key="err"
          >
            {{ err }}
          </p>
        </template>
      </div>
    </form>
    <template v-slot:footer="{ close }">
      <button
        type="button"
        class="button"
        v-on:click="close"
      >
        Cancel
      </button>
      <button
        type="submit"
        form="create-secret"
        class="button is-primary"
        :class="{ 'is-loading': loading }"
        :disabled="!canCreate || undefined"
        :title="buttonTitle"
      >
        Create
      </button>
    </template>
  </Modal>
</template>

<script lang="ts">
import { mapStores } from 'pinia'
import { defineComponent } from 'vue'

import Modal from '@/components/Modal.vue'
import { errorParser, extractFieldErrors, isArkindexError } from '@/helpers'
import { useNotificationStore, useSecretStore } from '@/stores'

export default defineComponent({
  components: {
    Modal,
  },
  data: () => ({
    loading: false,
    modal: false,
    name: '',
    content: '',
    errors: {} as { name?: string[]; content?: string[] },
  }),
  computed: {
    ...mapStores(useSecretStore, useNotificationStore),
    canCreate(): boolean {
      return !this.loading && this.name !== '' && this.content !== ''
    },
    buttonTitle(): string {
      if (this.loading) return 'Loading…'
      if (!this.canCreate) return 'The name and content must not be empty'
      return 'Create a new secret'
    },
  },
  methods: {
    toggleModal() {
      this.modal = !this.modal
    },
    async performCreate() {
      if (this.loading || !this.canCreate) return
      this.loading = true
      this.errors = {}
      try {
        await this.secretStore.create({ name: this.name, content: this.content })
        this.modal = false
        this.$router.push({ name: 'secret-details', params: { name: this.name.split('/') } })
      } catch (err) {
        let message: string = errorParser(err)
        if (isArkindexError(err)) {
          let otherErrors: string[]
          ;[this.errors, otherErrors] = extractFieldErrors(err.response.data, ['name', 'content'])
          message = otherErrors.length ? errorParser(otherErrors) : ''
        }
        if (message) this.notificationStore.notify({ type: 'error', text: message })
      } finally {
        this.loading = false
      }
    },
  },
})
</script>
